/**
 * Production-ready, ADA/WCAG-friendly CSSSlider (recommended pattern)
 *
 * What this version does:
 * ✅ Ensures active/visible slides remain in the accessibility tree (NVDA/JAWS/VO can read <img alt>)
 * ✅ Keyboard accessible controls (Prev/Next/Dots + optional Describe button + optional Pause/Play)
 * ✅ Announces only a short status on slide change: “Image X of Y”
 * ✅ Provides an explicit “Describe image” control that reads the current image’s alt/label on demand
 * ✅ Autoplay complies better: user can pause/play; any interaction pauses autoplay and it will NOT auto-resume
 * ✅ Reduced motion respected
 *
 * Why this is better than auto-reading alt every time:
 * - Blind users can read alt by navigating content normally (virtual cursor).
 * - Live regions that read full alt on every change often create noise/double announcements.
 * - “Describe image” gives equal access without forcing interruption.
 *
 * NOTE: This code assumes you have a `.visually-hidden` utility class.
 */

if (typeof CSSSlider !== "function") {
  class CSSSlider extends HTMLElement {
    constructor() {
      super();

      this._touchScreen = document.body.classList.contains("touchevents");
      this._rtl = document.documentElement.getAttribute("dir") === "rtl";

      this.o = {
        ...{
          selector: ".css-slide",
          snapping: true,
          groupCells: false,
          forceOneSlidePerPage: false,
          autoHeight: false,
          navigation: true,
          navigationDOM: `
            <button type="button" class="css-slider-button css-slider-prev" style="display:none"></button>
            <button type="button" class="css-slider-button css-slider-next" style="display:none"></button>`,
          thumbnails: true,
          thumbnailsDOM: `<div class="css-slider-dot-navigation" style="display:none"></div>`,
          thumbnailsNoInteraction: false,
          indexNav: false,
          indexNavDOM: `<div class="css-slider-index-navigation"><span class="css-slider-current">1</span> / <span class="css-slider-total">1</span></div>`,
          watchCSS: false,
          undisplay: false,
          disableSwipe: false,
          listenScroll: false,
          observer: true,
          disableMouseDownEvent: true,
          disableTabNavigation: false,

          autoplay: 0,
          // A11Y options
          landmark: false,
          ariaLabel: "Product gallery",
          announceChanges: true,           // announces short: "Image X of Y"
          arrowKeyNavigation: true,
          showAutoplayToggle: true,
          showDescribeButton: true,

          // What element inside a slide counts as the "media" for description
          // (Adjust if your markup differs.)
          mediaSelector: "img,[role='img'],video,model-viewer,iframe",
        },
        ...JSON.parse(this.dataset.options || "{}"),
      };

      if (!this.o.watchCSS) {
        this.initSlider();
      } else {
        this.RESIZE_WATCHER = debounce(() => {
          const afterContent = window.getComputedStyle(this, ":after").content;
          if (afterContent.includes("css-slide") && !this.sliderEnabled) {
            this.initSlider();
          } else if (!afterContent.includes("css-slide") && this.sliderEnabled) {
            this.destroySlider();
          }
        }, 100);

        window.addEventListener("resize", this.RESIZE_WATCHER);
        this.RESIZE_WATCHER();
      }
    }

    destroySlider() {
      this._teardownAutoplay();

      this.innerHTML = `${this.originalHTML}`;
      this.classList.remove("enabled");
      this.sliderEnabled = false;

      window.removeEventListener("resize", this.RESIZE_EVENT);
      this.element?.removeEventListener("scroll", this.SCROLL_EVENT);
      this.element?.removeEventListener("scroll", this.SCROLL_EVENT_ANIMATIONS);

      if (this.OBSERVER) {
        try { this.OBSERVER.disconnect(); } catch (_) {}
      }
    }

    initSlider() {
      this._readyEvent = new CustomEvent("ready");
      this._changeEvent = new CustomEvent("change");
      this._scrollEvent = new CustomEvent("scroll");
      this._navEvent = new CustomEvent("navigation");
      this._resetEvent = new CustomEvent("reset");

      this.classList.add("css-slider");
      this.originalHTML = this.innerHTML;

      this.innerHTML = `<div class="css-slider-viewport">
        <div class="css-slider-holder" ${this.o.disableTabNavigation ? 'tabindex="-1"' : ""}>
          <div class="css-slider-container">
            ${this.originalHTML}
          </div>
        </div>
      </div>`;

      if (this.o.undisplay) {
        this.querySelectorAll(`${this.o.selector}`).forEach((elm) => (elm.style.display = "block"));
      }

      if (this.o.selector !== ".css-slide") {
        this.querySelectorAll(`${this.o.selector}`).forEach((elm) => elm.classList.add("css-slide"));
      }

      this.element = this.querySelector(".css-slider-holder");
      if (this.o.groupCells) this.element.scrollLeft = 0;

      this.items = this.querySelectorAll(`${this.o.selector}`);
      this.indexedItems = [];
      this.index = 0; // page index
      this.length = this.items.length; // becomes page count in resetSlider()
      this.windowWidth = window.innerWidth;

      if (this.o.disableMouseDownEvent) {
        this.querySelector(".css-slider-container").addEventListener("mousedown", (e) => {
          if (e.target.tagName !== "SELECT") e.preventDefault();
        });
      }

      this.viewport = this.querySelector(".css-slider-viewport");
      if (this.o.autoHeight) this.viewport.classList.add("auto-height");
      // ---------- A11Y: semantics ----------
      // Most sliders are interactive groups, not page landmarks. Leaving them as
      // regions by default can create duplicate landmark violations on pages with
      // more than one carousel.
      this.setAttribute("role", this.o.landmark ? "region" : "group");
      this.setAttribute("aria-roledescription", "carousel");
      this.setAttribute("aria-label", this.o.ariaLabel || "Carousel");

      this._holderId =
        this.element.id || (this.element.id = `css-slider-holder-${Math.random().toString(36).slice(2)}`);

      if (!this.element.hasAttribute("tabindex") && !this.o.disableTabNavigation) {
        // focus target for recovery
        this.element.setAttribute("tabindex", "-1");
      }

      // live region: used for short status + on-demand description
      this._live = document.createElement("div");
      this._live.className = "visually-hidden";
      this._live.setAttribute("aria-live", "polite");
      this._live.setAttribute("aria-atomic", "true");
      this.appendChild(this._live);

      // ---------- Navigation / Controls ----------
      if (
        this.o.navigation ||
        this.o.thumbnails ||
        this.o.indexNav ||
        (parseInt(this.o.autoplay, 10) > 0 && this.o.showAutoplayToggle) ||
        this.o.showDescribeButton
      ) {
        const navigationElements = new DOMParser().parseFromString(this.o.navigationDOM, "text/html");
        const container = document.createElement("div");
        container.classList.add("css-slider-navigation-container");

        container.innerHTML = `
          ${(this.o.navigation ? navigationElements.querySelector(".css-slider-prev")?.outerHTML || "" : "")}
          ${(this.o.thumbnails ? this.o.thumbnailsDOM : "")}
          ${(this.o.navigation ? navigationElements.querySelector(".css-slider-next")?.outerHTML || "" : "")}
          ${(this.o.indexNav ? this.o.indexNavDOM : "")}
        `;

        if (this.o.navigation) {
          this.prevEl = container.querySelector(".css-slider-prev");
          this.nextEl = container.querySelector(".css-slider-next");

          const prevLabel = window.KROWN?.settings?.locales?.slider_prev_button_label || "Previous image";
          const nextLabel = window.KROWN?.settings?.locales?.slider_next_button_label || "Next image";

          this.prevEl.setAttribute("aria-label", prevLabel);
          this.nextEl.setAttribute("aria-label", nextLabel);
          this.prevEl.setAttribute("aria-controls", this._holderId);
          this.nextEl.setAttribute("aria-controls", this._holderId);

          this._wireButton(this.prevEl, () => {
            this._userInteracted();
            this.changeSlide("prev");
            this._announceStatus(); // short status only
            this.dispatchEvent(this._navEvent);
          });

          this._wireButton(this.nextEl, () => {
            this._userInteracted();
            this.changeSlide("next");
            this._announceStatus(); // short status only
            this.dispatchEvent(this._navEvent);
          });
        }

        if (this.o.thumbnails) this.thumbnailsEl = container.querySelector(".css-slider-dot-navigation");
        if (this.o.indexNav) {
          this.indexEl = container.querySelector(".css-slider-current");
          this.lengthEl = container.querySelector(".css-slider-total");
        }

        // Describe button (screen-reader discoverable, not visible)
        if (this.o.showDescribeButton) {
          this.describeEl = document.createElement("button");
          this.describeEl.type = "button";
          this.describeEl.className = "css-slider-describe visually-hidden";
          this.describeEl.setAttribute("aria-controls", this._holderId);
          this.describeEl.textContent =
            window.KROWN?.settings?.locales?.slider_describe_button_label || "Describe image";

          this.describeEl.addEventListener("click", () => {
            this._userInteracted();
            this._announceDescription();
          });

          container.appendChild(this.describeEl);
        }


        // Autoplay toggle
        if (parseInt(this.o.autoplay, 10) > 0 && this.o.showAutoplayToggle) {
          this.autoplayToggleEl = document.createElement("button");
          this.autoplayToggleEl.type = "button";
          this.autoplayToggleEl.className = "css-slider-autoplay-toggle";
          this.autoplayToggleEl.setAttribute("aria-controls", this._holderId);
          this.autoplayToggleEl.setAttribute("aria-pressed", "true");
          this.autoplayToggleEl.textContent =
            window.KROWN?.settings?.locales?.slider_pause_button_label || "Pause";

          this.autoplayToggleEl.addEventListener("click", () => {
            if (this._autoplayEnabled) this._pauseAutoplay(true);
            else this._resumeAutoplay(true);
          });

          container.appendChild(this.autoplayToggleEl);
        }

        this.append(container);
      }

      // ---------- Scroll & Interaction ----------
      if (this.items.length > 1) {
        if (this.o.observer) {
          this.OBSERVER = new IntersectionObserver(
            (entries) => {
              if (this._sliderBlockScroll) return;

              entries.forEach((entry) => {
                if (entry.intersectionRatio >= 0.5) {
                  const newIndex = parseInt(entry.target.getAttribute("data-index"), 10);
                  if (Number.isFinite(newIndex) && newIndex !== this.index) {
                    this.index = newIndex;
                    // Do not announce during passive scrolling/dragging; users can request description
                    this.checkSlide({ announce: false });
                    this.dispatchEvent(this._changeEvent);
                  }
                }
              });
            },
            { threshold: [0, 0.5] }
          );
        } else {
          this.SCROLL_EVENT = debounce(() => {
            if (this._sliderBlockScroll) return;

            const scrollItems = this.indexedItems.entries();
            const scrollArray = Array.from(scrollItems, (elm) =>
              Math.abs(elm[1].offsetLeft - this.element.scrollLeft)
            );
            const scrollDistance = Math.min(...scrollArray);
            const scrollIndex = scrollArray.indexOf(scrollDistance);

            if (scrollIndex !== this.index) {
              this.index = scrollIndex;
              this.checkSlide({ announce: false });
              this.dispatchEvent(this._changeEvent);
            }
          }, 10);

          this.element.addEventListener("scroll", this.SCROLL_EVENT, { passive: true });
        }

        this.RESIZE_EVENT = debounce(() => {
          if (this.windowWidth !== window.innerWidth && this.o.groupCells) this.resetSlider();
          if (!this.o.groupCells) this.checkSlide({ announce: false });
          this.windowWidth = window.innerWidth;
        }, 100);

        window.addEventListener("resize", this.RESIZE_EVENT);
        this.resetSlider(true);

        if (this.o.listenScroll) {
          this.SCROLL_EVENT_ANIMATIONS = () => {
            let slidesWidth = -this.querySelector(".css-slider-container").offsetWidth;
            this.items.forEach((elm) => (slidesWidth += elm.offsetWidth));
            this.progress = this.element.scrollLeft / slidesWidth;
            this.dispatchEvent(this._scrollEvent);
          };

          this.element.addEventListener("scroll", this.SCROLL_EVENT_ANIMATIONS, { passive: true });
        }

        // Arrow keys (when focus is inside slider)
        if (this.o.arrowKeyNavigation) {
          this.addEventListener("keydown", (e) => {
            const active = document.activeElement;
            if (!this.contains(active)) return;

            const tag = active?.tagName?.toLowerCase();
            if (tag === "input" || tag === "textarea" || tag === "select") return;

            if (e.key === "ArrowLeft") {
              e.preventDefault();
              this._userInteracted();
              this.changeSlide(this._rtl ? "next" : "prev");
              this._announceStatus();
            } else if (e.key === "ArrowRight") {
              e.preventDefault();
              this._userInteracted();
              this.changeSlide(this._rtl ? "prev" : "next");
              this._announceStatus();
            }
          });
        }

        // Mouse drag (kept from your behavior; adds focus to holder on mousedown)
        if (!this.o.disableSwipe && !this._touchScreen && !this.element.classList.contains("css-slider--singular")) {
          this.element.addEventListener("mousedown", () => {
            if (document.activeElement !== this.element && !this.element.contains(document.activeElement)) {
              this.element.focus({ preventScroll: true });
            }
          });

          this.element.addEventListener("mousedown", (e) => {
            if (!this.element.classList.contains("css-slider--disable-dragging")) {
              this.mouseX = e.screenX;
              this.element.classList.add("can-drag", "mouse-down");
            }
          });

          const clearDrag = () => {
            this.element.classList.remove("mouse-down", "can-drag", "pointer-events-off");
            if (this._pot) clearTimeout(this._pot);
          };

          this.element.addEventListener("mouseup", clearDrag);
          this.element.addEventListener("mouseleave", clearDrag);

          this.element.addEventListener("mousemove", (e) => {
            if (this.element.classList.contains("can-drag")) {
              const directionX = this.mouseX - e.screenX;
              if (Math.abs(directionX) > 1) {
                if (!this.element.classList.contains("css-slider--no-drag")) {
                  this.element.classList.add("pointer-events-off");
                }

                this._userInteracted();

                if ((!this._rtl && directionX > 0) || (this._rtl && directionX < 0)) {
                  this.changeSlide("next");
                  this.element.classList.remove("can-drag");
                } else if ((!this._rtl && directionX < 0) || (this._rtl && directionX > 0)) {
                  this.changeSlide("prev");
                  this.element.classList.remove("can-drag");
                }
              }
            }
          });
        }

        if (!this._touchScreen) {
          this.element.ondragstart = (e) => e.preventDefault();
        }

        // Autoplay: start, but any interaction pauses and it won't auto-resume
        if (parseInt(this.o.autoplay, 10) > 0) {
          this._autoplayEnabled = true;
          this._autoplayUserPaused = false;

          this.addEventListener("pointerdown", () => this._pauseAutoplay(true), { passive: true });
          this.addEventListener("keydown", () => this._pauseAutoplay(true));

          this._initAutoplay();
        }
      }

      this.classList.add("enabled");
      this.sliderEnabled = true;
      this.dispatchEvent(this._readyEvent);
    }

    // ---------- Helpers ----------
    _wireButton(el, onActivate) {
      if (!el) return;
      if (el.tagName.toLowerCase() === "button") {
        el.addEventListener("click", (e) => {
          e.preventDefault();
          onActivate();
        });
      } else {
        // fallback if a theme swaps markup
        el.setAttribute("role", "button");
        el.setAttribute("tabindex", "0");
        el.addEventListener("click", (e) => {
          e.preventDefault();
          onActivate();
        });
        el.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onActivate();
          }
        });
      }
    }

    _userInteracted() {
      if (parseInt(this.o.autoplay, 10) > 0) this._pauseAutoplay(true);
    }

    _announceStatus() {
      if (!this.o.announceChanges || !this._live) return;

      // Prefer item-based announcement even when paging:
      // Find the first visible slide (aria-hidden="false") after checkSlide runs.
      const visible = Array.from(this.items).filter((s) => s.getAttribute("aria-hidden") === "false");
      const first = visible[0] || null;

      let itemIndex = 0;
      if (first) itemIndex = Array.from(this.items).indexOf(first);

      const total = this.items.length;
      const current = Math.max(1, itemIndex + 1);

      this._live.textContent = `Image ${current} of ${total}`;
    }

    _announceDescription() {
      if (!this._live) return;

      const visible = Array.from(this.items).filter((s) => s.getAttribute("aria-hidden") === "false");
      // Prefer first visible slide (or fallback to snap anchor)
      const slide = visible[0] || this.indexedItems?.[this.index] || null;

      if (!slide) {
        this._live.textContent = "No image available.";
        return;
      }

      const media = slide.querySelector(this.o.mediaSelector);
      if (!media) {
        this._live.textContent = "No image description available.";
        return;
      }

      // If it's an <img>, alt is the primary source. Otherwise aria-label/title
      const alt = media.getAttribute("alt");
      const ariaLabel = media.getAttribute("aria-label");
      const title = media.getAttribute("title");

      // Decorative images often use alt="" — don't announce that as useful info.
      const desc = (alt !== null ? alt : "") || ariaLabel || title || "";

      if (!desc.trim()) {
        this._live.textContent = "No description provided for this image.";
        return;
      }

      this._live.textContent = desc;
    }

    // ---------- Slider logic ----------
    changeSlide(direction, behavior) {
      const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
      const finalBehavior = prefersReduced ? "auto" : behavior || "smooth";

      if (direction === "next") {
        if (this.index + 1 < this.length) this.index++;
      } else if (direction === "prev") {
        if (this.index - 1 >= 0) this.index--;
      } else if (parseInt(direction, 10) >= 0) {
        this.index = parseInt(direction, 10);
      }

      this._sliderBlockScroll = true;
      setTimeout(() => {
        this._sliderBlockScroll = false;
      }, 500);

      this.checkSlide({ announce: false });

      const left =
        this._rtl && this.slidesPerPage > 1
          ? (this.querySelector(".css-slider-container").offsetWidth -
              (this.indexedItems[this.index].offsetLeft -
                parseInt(getComputedStyle(this.indexedItems[0]).marginLeft, 10))) *
            -1
          : this.indexedItems[this.index].offsetLeft -
            parseInt(getComputedStyle(this.indexedItems[0]).marginLeft, 10);

      this.element.scrollTo({ top: 0, left, behavior: finalBehavior });
      this.dispatchEvent(this._changeEvent);
    }

    checkSlide({ announce = false } = {}) {
      if (this.o.navigation && this.prevEl && this.nextEl) {
        const atStart = this.index === 0;
        const atEnd = this.index === this.length - 1;

        this.prevEl.classList.toggle("disabled", atStart);
        this.nextEl.classList.toggle("disabled", atEnd);

        this.prevEl.setAttribute("aria-disabled", String(atStart));
        this.nextEl.setAttribute("aria-disabled", String(atEnd));

        if (this.prevEl.tagName.toLowerCase() === "button") this.prevEl.disabled = atStart;
        if (this.nextEl.tagName.toLowerCase() === "button") this.nextEl.disabled = atEnd;
      }

      if (this.o.thumbnails && this.thumbnails) {
        this.thumbnails.forEach((elm) => elm.classList.remove("active"));
        this.thumbnails[this.index]?.classList.add("active");
        this.thumbnails.forEach((dot, i) => dot.setAttribute("aria-current", i === this.index ? "true" : "false"));
      }

      if (this.o.indexNav && this.indexEl && this.lengthEl) {
        this.indexEl.textContent = this.index + 1;
        this.lengthEl.textContent = this.length;
      }

      if (this.o.autoHeight && this.indexedItems[this.index]) {
        this.viewport.style.height = `${this.indexedItems[this.index].offsetHeight}px`;
      }

      this.indexedItems.forEach((elm, i) => elm.classList.toggle("css-slide-active", i === this.index));

      // A11Y: hide non-visible slides from SR and keyboard
      if (!this.o.disableTabNavigation) {
        const maxStart = Math.max(0, this.items.length - this.slidesPerPage);
        const start = this.index * this.slidesPerPage;
        const adjustedStart = Math.min(start, maxStart);
        const end = adjustedStart + this.slidesPerPage;

        this.items.forEach((elm) => {
          if ("inert" in elm) elm.inert = true;
          elm.setAttribute("aria-hidden", "true");
          this._setFocusableDescendants(elm, false);
        });

        for (let i = adjustedStart; i < end && i < this.items.length; i++) {
          const elm = this.items[i];
          if ("inert" in elm) elm.inert = false;
          elm.setAttribute("aria-hidden", "false");
          this._setFocusableDescendants(elm, true);
        }

        // focus recovery if focus ends up in hidden slide
        const active = document.activeElement;
        if (active && this.contains(active)) {
          const slide = active.closest?.(this.o.selector);
          if (slide && slide.getAttribute("aria-hidden") === "true") {
            this.element.focus({ preventScroll: true });
          }
        }
      }

      if (announce) this._announceStatus();
    }

    _setFocusableDescendants(container, enabled) {
      const sel = "a[href],button,input,select,textarea,[tabindex]";
      const nodes = Array.from(container.querySelectorAll(sel));

      nodes.forEach((node) => {
        if (node === container) return;

        if (!enabled) {
          if (node.dataset.sliderTabManaged === "1") return;

          node.dataset.sliderTabManaged = "1";
          node.dataset.prevTabindex = node.hasAttribute("tabindex") ? node.getAttribute("tabindex") : "__none__";
          node.setAttribute("tabindex", "-1");
        } else {
          if (node.dataset.sliderTabManaged !== "1") return;

          const prev = node.dataset.prevTabindex;
          delete node.dataset.sliderTabManaged;
          delete node.dataset.prevTabindex;

          if (prev === "__none__") node.removeAttribute("tabindex");
          else node.setAttribute("tabindex", prev);
        }
      });
    }

    // ---------- Autoplay ----------
    _initAutoplay() {
      this._teardownAutoplay();
      this._autoplayEnabled = true;

      const interval = parseInt(this.o.autoplay, 10);
      if (!Number.isFinite(interval) || interval <= 0) return;

      this._autoplayInterval = setInterval(() => {
        if (!this._autoplayEnabled || this._autoplayUserPaused) return;

        if (this.index + 1 === this.length) this.index = 0;
        else this.index++;

        this.checkSlide({ announce: false });

        const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
        const left =
          this._rtl && this.slidesPerPage > 1
            ? (this.querySelector(".css-slider-container").offsetWidth -
                (this.indexedItems[this.index].offsetLeft -
                  parseInt(getComputedStyle(this.indexedItems[0]).marginLeft, 10))) *
              -1
            : this.indexedItems[this.index].offsetLeft -
              parseInt(getComputedStyle(this.indexedItems[0]).marginLeft, 10);

        this.element.scrollTo({ top: 0, left, behavior: prefersReduced ? "auto" : "smooth" });
      }, interval);

      if (this.autoplayToggleEl) {
        this.autoplayToggleEl.setAttribute("aria-pressed", "true");
        this.autoplayToggleEl.textContent = window.KROWN?.settings?.locales?.slider_pause_button_label || "Pause";
      }
    }

    _pauseAutoplay(fromUser) {
      if (parseInt(this.o.autoplay, 10) <= 0) return;
      this._autoplayEnabled = false;
      if (fromUser) this._autoplayUserPaused = true;

      if (this.autoplayToggleEl) {
        this.autoplayToggleEl.setAttribute("aria-pressed", "false");
        this.autoplayToggleEl.textContent = window.KROWN?.settings?.locales?.slider_play_button_label || "Play";
      }
    }

    _resumeAutoplay(fromUser) {
      if (parseInt(this.o.autoplay, 10) <= 0) return;
      this._autoplayEnabled = true;
      if (fromUser) this._autoplayUserPaused = false;

      // restart interval if it was cleared externally
      if (!this._autoplayInterval) this._initAutoplay();

      if (this.autoplayToggleEl) {
        this.autoplayToggleEl.setAttribute("aria-pressed", "true");
        this.autoplayToggleEl.textContent = window.KROWN?.settings?.locales?.slider_pause_button_label || "Pause";
      }
    }

    _teardownAutoplay() {
      if (this._autoplayInterval) clearInterval(this._autoplayInterval);
      this._autoplayInterval = null;
    }

    // ---------- Reset / paging ----------
    resetSlider(nojump = false, resetIndex = true) {
      let slidesWidth = 0;
      let page = 0;
      const totalWidth = this.querySelector(".css-slider-container").offsetWidth;
      let hideNavigation = false;

      this.slidesPerPage = 0;
      this.indexedItems = [];
      this.element.classList.add("disable-snapping");

      if (this.OBSERVER) this.OBSERVER.disconnect();

      // determine slidesPerPage
      this.items.forEach((elm, i) => {
        elm.classList.remove("css-slide--snap");
        slidesWidth += elm.getBoundingClientRect().width;
        if (slidesWidth > totalWidth && this.slidesPerPage === 0) {
          this.slidesPerPage = i;
        }
      });

      if (this.slidesPerPage === 0) {
        this.slidesPerPage = this.items.length;
        hideNavigation = true;
      }

      if (this.o.forceOneSlidePerPage) this.slidesPerPage = 1;

      // set snap/page anchors
      this.items.forEach((elm, i) => {
        if (i % this.slidesPerPage === 0) {
          elm.classList.add("css-slide--snap");
          elm.setAttribute("data-index", page++);
          if (this.OBSERVER) this.OBSERVER.observe(elm);
        }
      });

      this.indexedItems = this.querySelectorAll(`${this.o.selector}.css-slide--snap`);
      if (resetIndex) this.index = 0;

      // page count
      this.length = Math.ceil(this.items.length / this.slidesPerPage);

      // label anchors as slides/pages
      this.indexedItems.forEach((elm, i) => {
        elm.setAttribute("role", "group");
        elm.setAttribute("aria-roledescription", "slide");
        elm.setAttribute("aria-label", `Slide ${i + 1} of ${this.length}`);
      });

      // dots
      if (this.o.thumbnails && this.thumbnailsEl) {
        this.thumbnailsEl.innerHTML = "";

        for (let i = 0; i < this.length; i++) {
          const dot = document.createElement("button");
          dot.type = "button";
          dot.classList.add("css-slider-dot");
          dot.dataset.index = i;

          const label = (window.KROWN?.settings?.locales?.slider_thumbnail_label || "Go to slide {{ count }}").replace(
            "{{ count }}",
            i + 1
          );

          dot.setAttribute("aria-label", label);
          dot.setAttribute("aria-controls", this._holderId);

          if (!this.o.thumbnailsNoInteraction) {
            dot.addEventListener("click", (e) => {
              this._userInteracted();
              this.changeSlide(e.currentTarget.dataset.index);
              this._announceStatus();
            });
          } else {
            dot.disabled = true;
            dot.setAttribute("aria-disabled", "true");
          }

          this.thumbnailsEl.appendChild(dot);
        }

        this.thumbnailsEl.style.setProperty("--size", this.length);
        this.thumbnails = this.thumbnailsEl.querySelectorAll(".css-slider-dot");
      }

      // show/hide nav
      if (hideNavigation) {
        this.element.classList.add("css-slider--no-drag");
        if (this.o.navigation && this.prevEl && this.nextEl) {
          this.prevEl.style.display = "none";
          this.nextEl.style.display = "none";
        }
        if (this.o.thumbnails && this.thumbnailsEl) this.thumbnailsEl.style.display = "none";
      } else {
        this.element.classList.remove("css-slider--no-drag");
        if (this.o.navigation && this.prevEl && this.nextEl) {
          this.prevEl.style.display = "block";
          this.nextEl.style.display = "block";
        }
        if (this.o.thumbnails && this.thumbnailsEl) this.thumbnailsEl.style.display = "block";
      }

      this.checkSlide({ announce: false });

      if (!nojump) {
        this.element.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }

      this.element.classList.remove("disable-snapping");
      this.setAttribute("data-slider-length", this.length);
      this.dispatchEvent(this._resetEvent);
    }
  }

  if (typeof customElements.get("css-slider") === "undefined") {
    customElements.define("css-slider", CSSSlider);
  }

  document.addEventListener("shopify:section:load", (e) => {
    if (e.target.classList.contains("mount-css-slider") && e.target.querySelector("css-slider")) {
      e.target.querySelectorAll("css-slider").forEach((slider) => {
        if (slider.enabled) slider.resetSlider();
      });
    }
  });
}



